import React, { useCallback, useState } from 'react';
import Icon from './Icon';
import { COURIERS, CourierKey } from './couriers';

/* ---- courier badge ---- */
export function CourierBadge({ courier, lg }: { courier?: CourierKey | string | null; lg?: boolean }) {
  const c = courier ? COURIERS[courier as CourierKey] : undefined;
  if (!c) return null;
  return (
    <span className={`cbadge ${c.cls} ${lg ? 'lg' : ''}`}>
      <span className="dot" />
      {c.label}
    </span>
  );
}

/* ---- status pill ---- */
const STATUS: Record<string, [string, string, string]> = {
  packed: ['pill-ok', 'Spakowane', 'check'],
  completed: ['pill-ok', 'Spakowane', 'check'],
  inprogress: ['pill-info', 'W trakcie', 'loader'],
  in_progress: ['pill-info', 'W trakcie', 'loader'],
  packing: ['pill-info', 'W trakcie', 'loader'],
  pending: ['pill-warn', 'Oczekuje', 'clock'],
  not_started: ['pill-warn', 'Oczekuje', 'clock'],
  error: ['pill-err', 'Błąd', 'alert-circle'],
};
export function StatusPill({ status }: { status?: string }) {
  const [cls, lbl, ic] = STATUS[status || 'pending'] || STATUS.pending;
  return (
    <span className={`pill ${cls}`}>
      <Icon name={ic} size={12} /> {lbl}
    </span>
  );
}

/* ---- toggle ---- */
export function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return <div className={`toggle ${on ? 'on' : ''}`} onClick={() => onChange?.(!on)} role="switch" aria-checked={on} />;
}

/* ---- toasts ---- */
export type Toast = { id: string; msg: string; type: 'ok' | 'err' | 'info' };
export type PushToast = (msg: string, type?: Toast['type']) => void;

export function useToasts(): [Toast[], PushToast] {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback<PushToast>((msg, type = 'ok') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, type }]);
    // Błędy zostają na ekranie dłużej — operator na stacji musi zdążyć je przeczytać
    // (np. nieudana realizacja w Shopify). OK/info znikają szybko, by nie zaśmiecać widoku.
    const ttl = type === 'err' ? 8000 : 2600;
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ttl);
  }, []);
  return [toasts, push];
}

export function ToastHost({ toasts, kiosk }: { toasts: Toast[]; kiosk?: boolean }) {
  return (
    <div className={kiosk ? 'ktoast-host' : 'toast-host'}>
      {toasts.map((t) => (
        <div key={t.id} className={`${kiosk ? 'ktoast' : 'toast'} ${t.type}`}>
          <Icon name={t.type === 'ok' ? 'check-circle-2' : t.type === 'err' ? 'x-circle' : 'info'} size={18} /> {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ---- KPI ---- */
export function KpiCard({
  icon, label, value, delta, dir,
}: { icon: string; label: string; value: React.ReactNode; delta?: string; dir?: 'up' | 'down' }) {
  return (
    <div className="kpi">
      <div className="lbl"><Icon name={icon} size={15} /> {label}</div>
      <div className="val">{value}</div>
      {delta && (
        <div className={`dl ${dir || 'up'}`}>
          <Icon name={dir === 'down' ? 'trending-down' : 'trending-up'} size={13} /> {delta}
        </div>
      )}
    </div>
  );
}

export function Kpi({ icon, label, value, delta }: { icon: string; label: string; value: React.ReactNode; delta?: string }) {
  return (
    <div className="kpi-card">
      <div className="lbl"><Icon name={icon} size={14} /> {label}</div>
      <div className="val tnum">{value}</div>
      {delta && <div className="dl"><Icon name="trending-up" size={13} /> {delta}</div>}
    </div>
  );
}

/* ---- edge states ---- */
export function PageSkeleton({ table }: { table?: boolean }) {
  return (
    <div className="page" aria-busy="true">
      <div className="page-head">
        <div>
          <div className="skel" style={{ width: 220, height: 30 }} />
          <div className="skel" style={{ width: 320, height: 14, marginTop: 10 }} />
        </div>
      </div>
      <div className="kpi-row" style={{ marginBottom: 16 }}>
        {[0, 1, 2, 3].map((i) => (
          <div className="kpi" key={i}>
            <div className="skel" style={{ width: 90, height: 13 }} />
            <div className="skel" style={{ width: 70, height: 36, margin: '14px 0 8px' }} />
            <div className="skel" style={{ width: 60, height: 12 }} />
          </div>
        ))}
      </div>
      <div className="card card-pad">
        {table
          ? [0, 1, 2, 3, 4, 5].map((i) => <div className="skel" key={i} style={{ height: 40, marginBottom: 10 }} />)
          : <div className="skel" style={{ height: 320 }} />}
      </div>
    </div>
  );
}

export function EmptyState({
  icon = 'inbox', title = 'Brak danych', desc = 'Nie ma tu jeszcze nic do pokazania.', action, onAction,
}: { icon?: string; title?: string; desc?: string; action?: string; onAction?: () => void }) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="empty">
        <div className="ei"><Icon name={icon} size={28} /></div>
        <h3>{title}</h3>
        <p style={{ maxWidth: '38ch', margin: '0 auto' }}>{desc}</p>
        {action && (
          <button className="btn btn-primary" style={{ marginTop: 18 }} onClick={onAction}>
            <Icon name="plus" size={16} /> {action}
          </button>
        )}
      </div>
    </div>
  );
}

export function OfflineBanner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 28px', background: 'var(--warning-soft)', color: 'var(--warning-text)', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>
      <Icon name="cloud-off" size={16} />
      Tryb offline — dane z pamięci podręcznej. Zmiany są kolejkowane i zsynchronizują się po odzyskaniu połączenia.
    </div>
  );
}

/* ---- kiosk confirm dialog ---- */
export function ConfirmDialog({
  title, desc, confirmLabel, danger, onConfirm, onCancel,
}: { title: string; desc: string; confirmLabel: string; danger?: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="scrim center" onClick={onCancel}>
      <div className="sheet dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="sub">{desc}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="big-btn bb-ghost" style={{ flex: 1 }} onClick={onCancel}>Anuluj</button>
          <button className={`big-btn ${danger ? 'bb-danger' : 'bb-primary'}`} style={{ flex: 1 }} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ---- full screen scan feedback ---- */
export type Flash = { type: 'ok' | 'err'; msg: string; sub?: string } | null;
export function ScanFlash({ flash }: { flash: Flash }) {
  if (!flash) return null;
  return (
    <div className={`scan-flash show ${flash.type}`}>
      <div className="big-ic"><Icon name={flash.type === 'ok' ? 'check' : 'x'} size={72} /></div>
      <div className="fmsg">{flash.msg}</div>
      {flash.sub && <div className="fsub">{flash.sub}</div>}
    </div>
  );
}

/* ---- sound feedback (WebAudio) ---- */
export const AudioFX = (() => {
  let ctx: AudioContext | null = null;
  const ac = () => (ctx = ctx || new (window.AudioContext || (window as any).webkitAudioContext)());
  function tone(freq: number, dur: number, type: OscillatorType = 'sine', gain = 0.18) {
    try {
      const c = ac();
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = type;
      o.frequency.value = freq;
      o.connect(g);
      g.connect(c.destination);
      g.gain.setValueAtTime(gain, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
      o.start();
      o.stop(c.currentTime + dur);
    } catch (e) { /* dzwiek opcjonalny */ }
  }
  return {
    beep() { tone(880, 0.12, 'sine', 0.16); setTimeout(() => tone(1320, 0.1, 'sine', 0.13), 70); },
    buzz() { tone(160, 0.28, 'square', 0.13); },
    success() { tone(660, 0.1); setTimeout(() => tone(990, 0.16), 90); },
    click() { tone(420, 0.04, 'sine', 0.08); },
  };
})();
